import { useState } from "react";
import { Download, ExternalLink, Paperclip, Trash2 } from "lucide-react";

import { Button } from "@/atoms";
import { t, useGetTask, usePermissions, useTaskAttachments } from "@/hooks";
import type { TaskAttachmentInterface, TaskInterface } from "@/interfaces";
import { useAuthStore } from "@/store";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/ui";

interface Props {
    task: TaskInterface;
    patchTaskLocal: (id: string, updates: Partial<TaskInterface>) => void;
}

const STORAGE_BASE = (import.meta.env.VITE_STORAGE_URL as string | undefined) ?? "https://pm-bck.twindix.com/storage";

const getFileName = (att: TaskAttachmentInterface) => att.file_name ?? att.name ?? "file";
const getFileSize = (att: TaskAttachmentInterface): number => {
    const s = att.file_size ?? att.size;
    return typeof s === "string" ? parseInt(s, 10) : (s ?? 0);
};
const getFileUrl = (att: TaskAttachmentInterface): string => {
    if (att.file_url) return `${STORAGE_BASE}/${att.file_url}`;
    return att.url ?? att.dataUrl ?? "#";
};

const formatSize = (bytes: number) =>
    bytes < 1024 ? `${bytes} B` : bytes < 1048576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1048576).toFixed(1)} MB`;

export const TaskAttachments = ({ task, patchTaskLocal }: Props) => {
    const p = usePermissions();
    const canManage = p.tasks.manageAttachment(task);
    const { user: authUser } = useAuthStore();
    const currentUserId = authUser?.id ?? "";
    const { uploadHandler, deleteHandler } = useTaskAttachments();
    const { getHandler: getTaskHandler } = useGetTask();
    const attachments = task.attachments ?? [];
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewName, setPreviewName] = useState<string>("");

    const refreshAttachments = async () => {
        const fresh = await getTaskHandler(task.id);
        if (fresh) patchTaskLocal(task.id, { attachments: fresh.attachments ?? [] });
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = "";
        for (const file of files) {
            const ok = await uploadHandler(task.id, file);
            if (ok !== null) await refreshAttachments();
        }
    };

    const handleDelete = async () => {
        if (!confirmDeleteId) return;
        const ok = await deleteHandler(task.id, confirmDeleteId);
        if (ok) { await refreshAttachments(); setConfirmDeleteId(null); }
    };

    return (
        <div className="mt-4 pb-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-text-muted flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" />
                    {t("Attachments")}
                    {attachments.length > 0 && (
                        <span className="bg-muted rounded-full px-1.5 py-0.5 text-[10px] font-bold">{attachments.length}</span>
                    )}
                </p>
                {canManage && (
                    <label className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium cursor-pointer transition-colors">
                        <Paperclip className="h-3.5 w-3.5" />
                        {t("Upload")}
                        <input type="file" multiple className="hidden" onChange={handleFileChange} />
                    </label>
                )}
            </div>

            {attachments.length === 0 ? (
                canManage ? (
                    <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-5 cursor-pointer hover:border-primary hover:bg-primary-lighter/10 transition-colors">
                        <Paperclip className="h-5 w-5 text-text-muted" />
                        <p className="text-xs text-text-muted">{t("Click to upload files")}</p>
                        <input type="file" multiple className="hidden" onChange={handleFileChange} />
                    </label>
                ) : (
                    <p className="text-xs text-text-muted italic">{t("No attachments")}</p>
                )
            ) : (
                <div className="flex flex-col gap-2">
                    {attachments.map((att) => {
                        const fileUrl = getFileUrl(att);
                        const fileName = getFileName(att);
                        const fileSize = getFileSize(att);
                        const isImage = (att.type ?? att.file_name ?? "").match(/\.(png|jpe?g|gif|webp|svg)$/i);

                        return (
                            <div key={att.id} className="flex items-center gap-3 rounded-lg bg-muted p-2.5 group">
                                {isImage ? (
                                    <img src={fileUrl} alt={fileName} className="h-10 w-10 rounded object-cover shrink-0" />
                                ) : (
                                    <div className="h-10 w-10 rounded bg-primary-lighter flex items-center justify-center shrink-0">
                                        <Paperclip className="h-4 w-4 text-primary" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-text-dark truncate">{fileName}</p>
                                    <p className="text-[10px] text-text-muted">{formatSize(fileSize)}</p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        title={t("Open")}
                                        onClick={() => { setPreviewUrl(fileUrl); setPreviewName(fileName); }}
                                        className="p-1 rounded text-text-muted hover:text-primary hover:bg-primary-lighter/20 transition-colors cursor-pointer"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </button>
                                    <a
                                        href={fileUrl}
                                        download={fileName}
                                        title={t("Download")}
                                        className="p-1 rounded text-text-muted hover:text-primary hover:bg-primary-lighter/20 transition-colors cursor-pointer"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                    </a>
                                    {canManage && (att.uploaded_by?.id === currentUserId || p.tasks.delete()) && (
                                        <button
                                            onClick={() => setConfirmDeleteId(att.id)}
                                            title={t("Delete")}
                                            className="p-1 rounded text-text-muted hover:text-error hover:bg-error-light transition-colors cursor-pointer"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={!!previewUrl} onOpenChange={(open) => { if (!open) setPreviewUrl(null); }}>
                <DialogContent className="max-w-3xl w-full">
                    <DialogHeader><DialogTitle className="truncate text-sm">{previewName}</DialogTitle></DialogHeader>
                    <div className="flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-auto rounded-lg bg-muted">
                        {previewUrl && (() => {
                            const isImg = /\.(png|jpe?g|gif|webp|svg)$/i.test(previewName);
                            const isPdf = /\.pdf$/i.test(previewName);
                            if (isImg) return <img src={previewUrl} alt={previewName} className="max-w-full max-h-[65vh] object-contain rounded" />;
                            if (isPdf) return <iframe src={previewUrl} title={previewName} className="w-full h-[65vh] rounded border-0" />;
                            return (
                                <div className="flex flex-col items-center gap-3 p-8 text-text-muted">
                                    <Paperclip className="h-10 w-10" />
                                    <p className="text-sm">{t("Preview not available for this file type.")}</p>
                                    <a href={previewUrl} download={previewName} className="text-xs text-primary underline">{t("Download instead")}</a>
                                </div>
                            );
                        })()}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>{t("Delete Attachment")}</DialogTitle></DialogHeader>
                    <p className="text-sm text-text-muted mt-2">{t("This action cannot be undone.")}</p>
                    <div className="flex justify-end gap-2 mt-4">
                        <DialogClose asChild><Button variant="outline">{t("Cancel")}</Button></DialogClose>
                        <Button variant="destructive" onClick={handleDelete}>{t("Delete")}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
